/**
 * The operator's own details, as published on the Privacy notice (`WEB-019`).
 *
 * These four values are the only part of that page that is not derivable from this repository
 * or from `dealbid-core`'s schema. Everything else the notice says about what Dealbid stores
 * was read from the migrations and the source; this is who is storing it, and under which
 * law, and only the operator can say.
 *
 * They live in one module rather than inline in the page for two reasons: the Terms page will
 * need exactly the same four, and a test can assert that none of them is still a placeholder.
 * A privacy notice naming no controller is not a privacy notice — canon `WEB-019` refuses to
 * ship placeholder or sample legal text, and `test/sections/privacy.test.tsx` enforces it.
 */

/** The marker a pending value carries. Nothing containing it may ever be deployed. */
export const PENDING = "PENDING_OPERATOR_INPUT";

export interface OperatorDetails {
  /** The name published as the operator: a registered company, or a person trading as one. */
  name: string;
  /** A registered address where one is required, or `null` where none is. */
  address: string | null;
  /** The law this notice is governed by, as it should read in a sentence. */
  jurisdiction: string;
  /** The date the notice last changed, `D Month YYYY`. */
  lastUpdated: string;
}

export const PRIVACY_OPERATOR: OperatorDetails = {
  /*
   * Dealbid is operated by one person, not a company. Naming them is not optional: a privacy
   * notice that identifies no controller is not a privacy notice, and Google's OAuth review
   * reads this page.
   */
  name: "Zonayed Ahmed",
  /*
   * No postal address, deliberately. A solo operator's postal address is a home address, and
   * publishing one on a live page is permanent and indexed. The contact below is a monitored
   * role address, which is what a data-subject request or a reviewer actually needs.
   */
  address: null,
  /*
   * Bangladesh, because that is where the operator is. Naming a law with no connection to the
   * operator would be decorative: it would not prevent a claim where they live, and consumers
   * keep the protections of their own country regardless. The notice says exactly that, which
   * is what makes this clause unremarkable rather than adverse — see `PrivacyNotice`.
   */
  jurisdiction: "Bangladesh",
  lastUpdated: "2 September 2026",
};

/** Where a privacy question goes. A role address, monitored, never a personal one. */
export const PRIVACY_CONTACT = "privacy@dealbid.app";

/** Whether every published value has been supplied. */
export function operatorDetailsComplete(details: OperatorDetails): boolean {
  return ![details.name, details.address ?? "", details.jurisdiction, details.lastUpdated].some(
    (value) => value.includes(PENDING),
  );
}
